import { useScriptTag } from '#imports'
import { type Ref, computed, onMounted, ref } from 'vue'

import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { toRfc4646Locale } from '@backmarket/utils/string/toRfc4646Locale'

import { useTrustmark } from '../useTrustmark'

import { DEFAULT_WIDGET_CONFIG } from './TrustpilotBadge.config'

function useLoadTrustpilotLibrary(trustpilotWidgetRef: Ref) {
  const logger = useLogger()
  const loaded = ref(false)

  const url = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js'

  const { load } = useScriptTag(
    url,
    () => {
      window.Trustpilot?.loadFromElement(trustpilotWidgetRef.value)
    },
    { immediate: false },
  )

  onMounted(() => {
    load()
      .catch(() => {
        logger.error('[Truspilot] Failed to load script')
      })
      .finally(() => {
        loaded.value = true
      })
  })

  return loaded
}

function useTrustpilotConfig() {
  const logger = useLogger()
  const { market } = useMarketplace()
  const { withTrustpilot, trustmarkConfig } = useTrustmark()

  if (
    !withTrustpilot.value ||
    isEmpty(trustmarkConfig) ||
    !(
      'templateId' in trustmarkConfig &&
      'businessUnitId' in trustmarkConfig &&
      'href' in trustmarkConfig
    )
  ) {
    logger.error(
      '[Truspilot] Missing templateId, businessUnitId and/or href in trustmarkConfig',
    )

    return undefined
  }

  const locale = computed(() => toRfc4646Locale(market.defaultLocale))

  const configuration = computed(() => ({
    ...DEFAULT_WIDGET_CONFIG,
    'data-businessunit-id': trustmarkConfig.businessUnitId,
    'data-template-id': trustmarkConfig.templateId,
    'data-locale': locale.value,
    href: trustmarkConfig.href,
  }))

  return configuration
}

export function useTrustpilot(trustpilotWidgetRef: Ref) {
  const configuration = useTrustpilotConfig()
  const isLoaded = useLoadTrustpilotLibrary(trustpilotWidgetRef)

  return {
    configuration,
    isLoaded,
  }
}
